let _ = require('../_.js');

// Functionality dealing with making asynchronous programming flows easier to handle.


// Similar to _.setInterval, but calls an async function and 
// waits for it to complete before starting the loop again.
_.setAsyncInterval = function (callback, delay) {
    if (typeof callback !== 'function') {
        throw new Error('callback must be a function');
    }

    function scheduleNext() {
        setTimeout(async function () {
            try {
                await callback();
            } catch (error) {
                console.error(error);
            }
            scheduleNext();
        }, delay);
    }

    scheduleNext();
}

// Awaitable _.sleep function, useful for debugging
_.sleep = function (ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}


/**
 * @DECORATOR
 * 
 * This function, when invoked, immediately runs the callback exclusively. 
 * For subsequent invocations, it applies a delay before running the callback exclusively again. 
 * The delay starts after the current asynchronous operation resolves.
 * 
 * If 'delay' is set to 0, the function will only prevent enqueueing multiple executions of the 
 * same method more than once, but will still run them immediately in an exclusive sequential manner.
 * 
 * The most recent invocation of the function will be the paramaters that get passed to the function
 * when it invokes.
 *
 * The function returns a promise that resolves when the next exclusive execution completes.
 *
 * @param {number} delay The delay in milliseconds before subsequent invocations
 * @param {function} callback The callback function to be invoked
 * @return {function} A function that when invoked, runs the callback immediately and exclusively, 
 * with subsequent invocations being delayed and run exclusively in order
 */
_.debounce = function (delay, callback) {
    let that = this;

    let nextTimer = null;
    let lastInvocationTime = 0;
    let queued = false;
    let resolveQueue = [];
    let rejectQueue = [];
    let nextArgs = {};
    let running = false;
    let timeSinceLastInvocation = null;

    const runFunction = async () => {
        // Clear the queue settings
        let thisResolveQueue = resolveQueue;
        let thisRejectQueue = rejectQueue;
        let thisArgs = nextArgs;
        resolveQueue = [];
        rejectQueue = [];
        nextArgs = {};
        queued = false;
        running = true;

        try {
            const result = await callback(...thisArgs);
            thisResolveQueue.forEach(resolve => resolve(result));
        } catch (err) {
            console.error("Error during execution:", err);
            thisRejectQueue.forEach(reject => reject(err));
        } finally {
            running = false;
            lastInvocationTime = Date.now(); // Update the time after function completes
            if (queued) {
                // Schedule next run only if there are queued invocations
                const timeSinceLastInvocation = Date.now() - lastInvocationTime;
                const delayAdjustment = Math.max(delay - timeSinceLastInvocation, 0);
                clearTimeout(nextTimer);
                nextTimer = setTimeout(runFunction, delayAdjustment);
            }
        }
    };

    return (...args) => {
        return new Promise((resolve, reject) => {
            resolveQueue.push(resolve);
            rejectQueue.push(reject);
            nextArgs = args; // Storing the latest arguments

            if (!running && !queued) {
                if (!lastInvocationTime) {
                    clearTimeout(nextTimer);
                    nextTimer = setTimeout(runFunction, 0);
                } else {
                    timeSinceLastInvocation = Date.now() - lastInvocationTime;
                    if (timeSinceLastInvocation >= delay) {
                        // If sufficient time has passed since the last invocation, run immediately
                        clearTimeout(nextTimer);
                        nextTimer = setTimeout(runFunction, 0);
                    } else {
                        // Otherwise, schedule it after the remaining delay
                        queued = true;
                        clearTimeout(nextTimer);
                        nextTimer = setTimeout(runFunction, delay - timeSinceLastInvocation);
                    }
                }
            } else if (!queued) {
                // Otherwise, schedule it after the remaining delay
                queued = true;
                clearTimeout(nextTimer);
                nextTimer = setTimeout(runFunction, delay - timeSinceLastInvocation);
            }
        });
    };
};


/**
 * @DECORATOR
 *
 * Runs the contained callback in an advisory lock - only one invocation at a time.
 * Subsequent calls queue and execute sequentially.
 */
_.exclusive = function (callback) {
    let queue = [];
    let isRunning = false;

    const run = async () => {
        if (queue.length > 0) {
            let [resolve, args] = queue.shift();
            try {
                let result = await callback(...args);
                resolve(result);
            } catch (e) {
                resolve(Promise.reject(e));
            } finally {
                isRunning = false;
            }
            if (queue.length > 0) {
                run();
            } else {
                isRunning = false;
            }
        }
    };

    return (...args) => {
        return new Promise((resolve) => {
            queue.push([resolve, args]);
            if (!isRunning) {
                isRunning = true;
                run();
            }
        });
    };
};


/**
 * Named advisory mutex locks. _.mutex(name, callback) executes callback exclusively --
 * only one callback per lock name runs at a time. Subsequent calls queue and execute
 * sequentially after the current one resolves.
 *
 * _.mutex_read(name, callback) runs callbacks concurrently but blocks while a mutex()
 * write lock is held. When all reads finish, queued writes proceed.
 */
let _mutex_locks = {};

function _get_lock(name) {
    if (!_mutex_locks[name]) {
        _mutex_locks[name] = {
            write_queue: [],
            write_locked: false,
            read_queue: [],
            read_count: 0,
        };
    }
    return _mutex_locks[name];
}

_.mutex = function (lock_name, callback) {
    let lock = _get_lock(lock_name);
    return new Promise((resolve) => {
        lock.write_queue.push([callback, resolve]);
        _mutex_process_writes(lock_name);
    });
};

_.mutex_force_unlock = function (lock_name) {
    let lock = _get_lock(lock_name);
    lock.write_locked = false;
    lock.read_count = 0;
    _mutex_process_writes(lock_name);
};

_.mutex_read = function (lock_name, callback) {
    let lock = _get_lock(lock_name);
    return new Promise((resolve) => {
        lock.read_queue.push([callback, resolve]);
        _mutex_process_reads(lock_name);
    });
};

async function _mutex_process_writes(lock_name) {
    let lock = _get_lock(lock_name);
    if (lock.read_count > 0 || lock.write_locked || lock.write_queue.length === 0) {
        return;
    }

    lock.write_locked = true;

    while (lock.write_queue.length) {
        let [callback, resolve] = lock.write_queue.shift();
        try {
            let result = await callback();
            resolve(result);
        } catch (e) {
            console.error('[mutex] Error in lock "' + lock_name + '":', e);
            resolve(undefined);
        }
    }

    lock.write_locked = false;
    _mutex_process_reads(lock_name);
}

async function _mutex_process_reads(lock_name) {
    let lock = _get_lock(lock_name);
    if (lock.read_queue.length === 0 || lock.write_locked) {
        return;
    }

    let promises = [];
    while (lock.read_queue.length) {
        lock.read_count++;
        let [callback, resolve] = lock.read_queue.shift();
        promises.push((async function () {
            try {
                resolve(await callback());
            } catch (e) {
                console.error('[mutex_read] Error in lock "' + lock_name + '":', e);
                resolve(undefined);
            } finally {
                lock.read_count--;
            }
        })());
    }

    await Promise.all(promises);
    _mutex_process_writes(lock_name);
}

